import React from 'react';
import {
  Paper,
  Grid,
  Typography,
  Box,
  alpha,
  useTheme,
  Card,
  CardContent,
  Chip,
  Alert
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  Cell,
  PieChart,
  Pie
} from 'recharts';
import {
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const RelatorioTempoEspera = ({ dados, customTheme }) => {
  const theme = useTheme();

  // Verificar se temos dados
  if (!dados || !dados.tempoMedioEspera) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        Nenhum dado de tempo de espera disponível para o período selecionado.
      </Alert>
    );
  }

  // Usar dados reais se disponíveis
  const distribuicaoTempos = dados.distribuicaoTempos || [];
  const picosEspera = dados.picosEspera || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Baixo': return '#4caf50';
      case 'Normal': return '#2196f3';
      case 'Médio': return '#ff9800';
      case 'Alto': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color }}
            >
              {entry.name}: {entry.value} min
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Grid container spacing={3}>
      {/* Cards de Métricas de Tempo */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Análise de Tempos de Espera
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha('#4caf50', 0.2)}`,
                background: `linear-gradient(135deg, ${alpha('#4caf50', 0.05)} 0%, ${alpha('#4caf50', 0.02)} 100%)`,
              }}
            >
              <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                <ScheduleIcon sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
                  {dados.tempoMedioEsperaGeral || '0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tempo Médio Geral (min)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha('#ff9800', 0.2)}`,
                background: `linear-gradient(135deg, ${alpha('#ff9800', 0.05)} 0%, ${alpha('#ff9800', 0.02)} 100%)`,
              }}
            >
              <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                <TrendingUpIcon sx={{ fontSize: 48, color: '#ff9800', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800' }}>
                  {dados.picoMaximo || '0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pico Máximo (min)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha('#2196f3', 0.2)}`,
                background: `linear-gradient(135deg, ${alpha('#2196f3', 0.05)} 0%, ${alpha('#2196f3', 0.02)} 100%)`,
              }}
            >
              <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                <TrendingDownIcon sx={{ fontSize: 48, color: '#2196f3', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196f3' }}>
                  {dados.tempoMinimo || '0'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tempo Mínimo (min)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      {/* Gráfico de Área - Evolução dos Tempos */}
      <Grid item xs={12} lg={8}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
            height: 400
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Evolução dos Tempos de Espera por Horário
          </Typography>
          
          <ResponsiveContainer width="100%" height="85%">
            <AreaChart data={dados.tempoMedioEspera}>
              <defs>
                <linearGradient id="colorTempo" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={customTheme.palette.primary.main} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={customTheme.palette.primary.main} stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorMaximo" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ff9800" stopOpacity={0.6}/>
                  <stop offset="95%" stopColor="#ff9800" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={alpha(theme.palette.text.primary, 0.1)}
              />
              <XAxis 
                dataKey="hora"
                stroke={theme.palette.text.secondary}
                fontSize={12}
              />
              <YAxis 
                stroke={theme.palette.text.secondary}
                fontSize={12}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="maximo"
                stackId="1"
                stroke="#ff9800"
                fill="url(#colorMaximo)"
                name="Tempo Máximo"
              />
              <Area
                type="monotone"
                dataKey="tempoMedio"
                stackId="2"
                stroke={customTheme.palette.primary.main}
                fill="url(#colorTempo)"
                name="Tempo Médio"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Paper>
      </Grid>

      {/* Distribuição de Faixas de Tempo */}
      <Grid item xs={12} lg={4}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
            height: 400
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Distribuição por Faixas de Tempo
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            {distribuicaoTempos.map((faixa, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {faixa.faixa}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {faixa.quantidade} ({faixa.percentual}%)
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 12,
                    bgcolor: alpha(faixa.cor, 0.1),
                    borderRadius: 6,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: `${faixa.percentual}%`,
                      height: '100%',
                      bgcolor: faixa.cor,
                      borderRadius: 6,
                      transition: 'width 1s ease-in-out'
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Grid>

      {/* Análise por Serviço */}
      <Grid item xs={12}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Tempos de Espera por Serviço
          </Typography>
          
          <Grid container spacing={3}>
            {picosEspera.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(getStatusColor(item.status), 0.2)}`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                      {item.servico}
                    </Typography>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 700, 
                        color: getStatusColor(item.status),
                        mb: 1 
                      }}
                    >
                      {item.tempo} min
                    </Typography>
                    <Chip
                      label={item.status}
                      size="small"
                      sx={{
                        bgcolor: alpha(getStatusColor(item.status), 0.1),
                        color: getStatusColor(item.status),
                        fontWeight: 600
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Grid>

      {/* Insights e Recomendações */}
      <Grid item xs={12}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
            background: theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${alpha(customTheme.palette.primary.main, 0.05)} 0%, ${alpha(customTheme.palette.primary.main, 0.02)} 100%)`
              : `linear-gradient(135deg, ${alpha(customTheme.palette.primary.main, 0.05)} 0%, ${alpha(customTheme.palette.primary.main, 0.02)} 100%)`
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Insights e Recomendações
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" paragraph>
                <strong>✅ Pontos Positivos:</strong>
              </Typography>
              <Typography variant="body2" paragraph sx={{ ml: 2 }}>
                • 51.5% dos atendimentos concluídos em até 10 minutos
              </Typography>
              <Typography variant="body2" paragraph sx={{ ml: 2 }}>
                • Tempo médio geral de 8.5 minutos está dentro do ideal
              </Typography>
              <Typography variant="body2" paragraph sx={{ ml: 2 }}>
                • Horários matutinos com melhor performance
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="body1" paragraph>
                <strong>⚠️ Áreas de Melhoria:</strong>
              </Typography>
              <Typography variant="body2" paragraph sx={{ ml: 2 }}>
                • Horário de 12:00 apresenta pico de espera (35 min)
              </Typography>
              <Typography variant="body2" paragraph sx={{ ml: 2 }}>
                • 7.3% dos atendimentos excedem 20 minutos
              </Typography>
              <Typography variant="body2" paragraph sx={{ ml: 2 }}>
                • Protocolo requer atenção especial (22.5 min médio)
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default RelatorioTempoEspera; 